import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Artwork } from '../../types/Artwork';
import { ImageCard } from './ImageCard';

interface GalleryStripProps {
    artworks: Artwork[];
    sectionPath: string[];
    /** When returning via "back to gallery", restore the last horizontal offset for this
     *  section instead of resetting to the beginning. */
    restoreScroll?: boolean;
}

const PER_VIEW = 4;

// Remembers each section's horizontal scroll offset across an in-session visit to a viewing
// room, so "back to gallery" can return the visitor exactly where they left off.
const savedScroll = new Map<string, number>();

// Desktop gallery: a horizontal scroll-snap strip showing ~4 tiles per view, with
// a pager whose prev/next nudge one viewport and whose label tracks the scroll position.
export const GalleryStrip = ({ artworks, sectionPath, restoreScroll = false }: GalleryStripProps) => {
    const stripRef = useRef<HTMLDivElement>(null);
    const [page, setPage] = useState(1);
    const totalPages = Math.max(1, Math.ceil(artworks.length / PER_VIEW));
    const pathKey = sectionPath.join('/');

    // Sync the pager label to the strip's current offset (no side effects).
    const syncPage = useCallback(() => {
        const el = stripRef.current;
        if (!el || el.clientWidth === 0) return;
        setPage(Math.min(totalPages, Math.max(1, Math.round(el.scrollLeft / el.clientWidth) + 1)));
    }, [totalPages]);

    const onScroll = useCallback(() => {
        const el = stripRef.current;
        if (!el || el.clientWidth === 0) return;
        savedScroll.set(pathKey, el.scrollLeft); // remember position while browsing
        syncPage();
    }, [pathKey, syncPage]);

    // On entering a section: restore the saved offset when returning via "back to gallery",
    // otherwise reset to the beginning (navigating to a *different* gallery). The strip DOM node
    // is reused across section changes, so its scrollLeft would otherwise carry over. Keyed on the
    // section path, so scrolling within the same gallery is preserved; useLayoutEffect applies
    // before paint (no flash of the new works at the old offset).
    useLayoutEffect(() => {
        const el = stripRef.current;
        if (!el) return;
        el.scrollLeft = restoreScroll ? savedScroll.get(pathKey) ?? 0 : 0;
        syncPage();
    }, [pathKey, restoreScroll, syncPage]);

    const nudge = (direction: 1 | -1) => {
        const el = stripRef.current;
        if (!el) return;
        const reduced =
            typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        el.scrollBy({ left: direction * el.clientWidth, behavior: reduced ? 'auto' : 'smooth' });
    };

    return (
        <div className="flex h-full flex-col">
            <div
                ref={stripRef}
                onScroll={onScroll}
                role="region"
                aria-label="Artworks — scroll horizontally"
                tabIndex={0}
                className="gallery-strip flex snap-x snap-mandatory gap-3 overflow-x-auto pb-3"
            >
                {artworks.map((artwork) => (
                    <div key={artwork.id} className="shrink-0 basis-[calc(25%-9px)] snap-start">
                        <ImageCard artwork={artwork} sectionPath={sectionPath} />
                    </div>
                ))}
            </div>

            <div className="flex items-center justify-end gap-4 pt-5">
                <span className="text-meta italic text-tone-800">
                    page {page} / {totalPages}
                </span>
                <button type="button" className="btn" onClick={() => nudge(-1)} aria-label="Previous page">
                    ‹ previous
                </button>
                <button type="button" className="btn" onClick={() => nudge(1)} aria-label="Next page">
                    next ›
                </button>
            </div>
        </div>
    );
};
